// Incident photo upload/list helpers — the incident-photos bucket + the
// incident_photos metadata table. Adapted from jobPhotos.ts, with two
// deliberate differences for the evidence posture:
//   * uploads run through transcodeImageToWebP FIRST (the established image
//     pipeline: HEIC → WebP, compressed), so the stored object is always .webp.
//   * there is NO deleteIncidentPhoto — photos are evidence and the bucket has
//     no DELETE policy. Removal is intentionally impossible from the client.
//
// Workspace isolation is enforced at both layers (storage.objects RLS on
// foldername[1] + incident_photos RLS on workspace_id); the workspaceId arg
// only BUILDS the path — the policies are the authority.

import { supabase } from '@/lib/supabase'
import { transcodeImageToWebP } from '@/lib/imageTranscode'

const BUCKET = 'incident-photos'

// Accept the same input types the transcoder can handle; everything lands as
// WebP after transcodeImageToWebP. Exported for the <input accept> attribute.
export const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'] as const
export const MAX_PHOTO_BYTES = 15 * 1024 * 1024 // 15MB — matches the bucket cap
export const PHOTO_ACCEPT_ATTR = 'image/jpeg,image/png,image/webp,image/heic,image/heif'

export interface IncidentPhoto {
  id: string
  workspace_id: string
  incident_id: string
  storage_path: string
  caption: string | null
  byte_size: number | null
  uploaded_by: string | null
  uploaded_at: string
}

export type UploadResult =
  | { data: IncidentPhoto; error: null }
  | { data: null; error: Error }

/**
 * Transcode → upload → insert the metadata row. On row-insert failure,
 * best-effort remove the just-uploaded object (though the bucket has no client
 * DELETE policy, the service-role path used by remove() still can — this only
 * runs on the error path to avoid orphans, never as a user-facing delete).
 */
export async function uploadIncidentPhoto({
  workspaceId, incidentId, file, caption,
}: { workspaceId: string; incidentId: string; file: File; caption?: string }): Promise<UploadResult> {
  let optimized: File
  try {
    optimized = await transcodeImageToWebP(file)
  } catch (e) {
    return { data: null, error: new Error(`Could not process image: ${e instanceof Error ? e.message : 'unknown'}`) }
  }

  const objectId = crypto.randomUUID()
  const path = `${workspaceId}/${incidentId}/${objectId}.webp`

  const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, optimized, {
    cacheControl: '3600',
    contentType: 'image/webp',
  })
  if (upErr) return { data: null, error: new Error(`Upload failed: ${upErr.message}`) }

  const { data: userData } = await supabase.auth.getUser()
  const uploadedBy = userData?.user?.id ?? null

  const { data: row, error: dbErr } = await supabase
    .from('incident_photos')
    .insert({
      workspace_id: workspaceId,
      incident_id: incidentId,
      storage_path: path,
      caption: caption ?? null,
      uploaded_by: uploadedBy,
      byte_size: optimized.size,
    })
    .select()
    .single()

  if (dbErr || !row) {
    await supabase.storage.from(BUCKET).remove([path]).catch(() => { /* swallow — orphan cleanup only */ })
    return { data: null, error: new Error(dbErr?.message ?? 'Row insert failed') }
  }
  return { data: row as IncidentPhoto, error: null }
}

/** Short-lived signed URL (5 min default) for display. */
export async function getIncidentPhotoSignedUrl(storagePath: string, ttlSeconds = 300): Promise<{ url: string | null; error: Error | null }> {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(storagePath, ttlSeconds)
  if (error || !data?.signedUrl) return { url: null, error: error ?? new Error('No signed URL returned') }
  return { url: data.signedUrl, error: null }
}

/** Photos for an incident, newest first. RLS scopes to the caller's workspace. */
export async function listIncidentPhotos(incidentId: string): Promise<{ data: IncidentPhoto[]; error: Error | null }> {
  const { data, error } = await supabase
    .from('incident_photos')
    .select('*')
    .eq('incident_id', incidentId)
    .order('uploaded_at', { ascending: false })
  if (error) return { data: [], error: new Error(error.message) }
  return { data: (data ?? []) as IncidentPhoto[], error: null }
}

// NO deleteIncidentPhoto — deliberately absent. Incident photos are evidence;
// the bucket has no client DELETE policy and incident_photos has no DELETE
// policy. Nothing here can remove them.
